/**
 * FieldRenderer Component
 * Renders regulation detail fields based on configuration
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import { apiClient } from '../../../services/apiClient';
import {
  isDynamicAutoTagsField,
  isRegulationCategoriesFieldName,
  visibleProductTypes,
} from '../../../services/regulationsService';
import { autoTagEmptyDisplay } from '../../../services/regulationAutoTags';
import { AutoTagDisplayLabel } from '../../shared/AutoTagDisplayLabel';
import type { FieldConfig, AssessmentDocument } from '../../../types';

interface TagListWithViewAllProps {
  items: any[];
  field: FieldConfig;
  isClickable: boolean;
  maxRows?: number;
  onViewAll?: (field: FieldConfig, items: any[], startIndex?: number) => void;
  isCategories: boolean;
  /** When true, View All only appears after chips overflow maxRows. */
  viewAllOnlyWhenOverflow?: boolean;
}

const CHIP_HEIGHT = 24;
const CHIP_GAP = 8;

const getSubItems = (item: any) =>
  item.sub_categories || item.sub_markets || item.sub_items || item.children || [];

/**
 * Flatten category tree for summary chips: show every leaf node (deepest items with a name).
 * Skips the synthetic "All" wrapper and walks children/sub_categories/etc.
 */
const flattenCategoryTagsForDisplay = (
  items: any[],
): { flat: any[]; level1IndexByFlat: number[] } => {
  const flat: any[] = [];
  const level1IndexByFlat: number[] = [];

  const visit = (item: any, level1Index: number): void => {
    const subItems = getSubItems(item);
    const name = typeof item === 'string' ? item : item.name || '';

    if (!Array.isArray(subItems) || subItems.length === 0) {
      if (typeof item === 'string' || name) {
        flat.push(item);
        level1IndexByFlat.push(level1Index);
      }
      return;
    }

    if (name === 'All') {
      subItems.forEach((child) => visit(child, level1Index));
      return;
    }

    subItems.forEach((child) => visit(child, level1Index));
  };

  items.forEach((level1, index) => visit(level1, index));
  return { flat, level1IndexByFlat };
};

const TagListWithViewAll: React.FC<TagListWithViewAllProps> = ({
  items,
  field,
  isClickable,
  maxRows,
  onViewAll,
  isCategories,
  viewAllOnlyWhenOverflow = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const prevItemsRef = useRef(items);

  if (prevItemsRef.current !== items) {
    prevItemsRef.current = items;
    if (visibleCount !== null) {
      setVisibleCount(null);
    }
  }

  const isOverflowing =
    visibleCount !== null && visibleCount < items.length;
  const showViewAllChip =
    isClickable && (!viewAllOnlyWhenOverflow || isOverflowing);

  const chipSx = {
    borderRadius: '16px',
    height: '24px',
    backgroundColor: '#f3f4f6',
    border: 'none',
    color: 'text.primary',
    fontSize: (theme) => theme.typography.body2.fontSize,
    cursor: showViewAllChip ? 'pointer' : 'default',
    '& .MuiChip-label': { px: 1.5 },
    '&:hover': showViewAllChip ? { backgroundColor: '#e5e7eb' } : {},
  };

  const viewAllChipSx = {
    borderRadius: '16px',
    height: '24px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #f3f4f6',
    color: 'primary.main',
    fontSize: (theme) => theme.typography.body2.fontSize,
    fontWeight: 600,
    cursor: 'pointer',
    '& .MuiChip-label': { px: 1.5 },
    '&:hover': { backgroundColor: '#dbeafe' },
  };

  const getLabel = (item: any): React.ReactNode => {
    if (typeof item === 'string') return item;
    const name = item.name || '';
    if (isCategories) return name;
    const subItems = item.sub_categories || item.sub_markets || item.sub_items || item.children || [];
    const count = Array.isArray(subItems) ? subItems.length : 0;
    if (count <= 0) return name;
    return (
      <span>
        {name}
        <Box component="span" sx={{ color: '#97a3bc' }}>{` (${count})`}</Box>
      </span>
    );
  };

  const baseItems = items;

  useLayoutEffect(() => {
    if (!maxRows || !containerRef.current || visibleCount !== null) return;

    const container = containerRef.current;
    const children = Array.from(container.children) as HTMLElement[];
    if (children.length === 0) return;

    const maxH = maxRows * CHIP_HEIGHT + (maxRows - 1) * CHIP_GAP;
    let cutoff = children.length;

    for (let i = 0; i < children.length; i++) {
      const chipTop = children[i].offsetTop - container.offsetTop;
      if (chipTop + CHIP_HEIGHT > maxH) {
        cutoff = i;
        break;
      }
    }

    if (cutoff >= children.length) {
      setVisibleCount(baseItems.length);
    } else {
      // Reserve one slot for View All when it will be shown (always, or on overflow).
      const reserveViewAllSlot = isClickable;
      const tagLimit = reserveViewAllSlot ? cutoff - 1 : cutoff;
      setVisibleCount(Math.max(tagLimit, 0));
    }
  }, [visibleCount, baseItems.length, maxRows, isClickable]);

  const displayItems = visibleCount !== null
    ? baseItems.slice(0, visibleCount)
    : baseItems;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {displayItems.map((item, idx) => (
        <Chip
          key={idx}
          label={getLabel(item)}
          onClick={() => {
            if (showViewAllChip && onViewAll) {
              onViewAll(field, items, idx);
            }
          }}
          sx={chipSx}
        />
      ))}
      {showViewAllChip && (
        <Chip
          label={'View All'}
          onClick={() => {
            if (onViewAll) {
              onViewAll(field, items);
            }
          }}
          sx={viewAllChipSx}
        />
      )}
    </Box>
  );
};

interface FieldRendererProps {
  field: FieldConfig;
  value: unknown;
  onDocumentClick?: (doc: AssessmentDocument) => void;
  onDocumentDownload?: (doc: AssessmentDocument) => void;
  onViewAll?: (field: FieldConfig, items: any[], startIndex?: number) => void;
  regulationId?: string;
  /** Summary top row: date values shown in bold to match regulation detail layout */
  emphasizeDateValue?: boolean;
  /** Tighter vertical spacing when nested in summary grids */
  denseLayout?: boolean;
  /** Summary grid: product types as comma-separated text, max 2 lines + ellipsis, full text in tooltip */
  productTypesSummaryTwoLineClamp?: boolean;
  /**
   * When set, empty dynamic auto-tag concept values use auto_tags empty-state
   * copy ("-" vs "NA") instead of always showing "-".
   */
  autoTagsFieldPresent?: boolean;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onDocumentClick,
  onDocumentDownload,
  onViewAll,
  regulationId,
  emphasizeDateValue = false,
  denseLayout = false,
  productTypesSummaryTwoLineClamp = false,
  autoTagsFieldPresent,
}) => {
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize flattened category tags to keep stable reference across renders
  const categoryDisplayMeta = useMemo(() => {
    if (
      isRegulationCategoriesFieldName(field.field_name) &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      const { flat, level1IndexByFlat } = flattenCategoryTagsForDisplay(
        value as any[],
      );
      // Fallback to Level 1 when tree has no traversable children
      if (flat.length > 0) {
        return { flat, level1IndexByFlat };
      }
      const fallback = value as any[];
      return {
        flat: fallback,
        level1IndexByFlat: fallback.map((_, i) => i),
      };
    }
    return undefined;
  }, [field.field_name, value]);

  const flattenedCategoryItems = categoryDisplayMeta?.flat;

  // Fields that should always display even if empty (show "-" instead)
  const alwaysShowFields = [
    'release_date',
    'published_date',
    'coming_into_force_date',
    'effective_date',
    'product_types',
    'associated_documents',
  ];

  const dateValueTypographySx = emphasizeDateValue
    ? {
        fontSize: (theme) => theme.typography.body2.fontSize,
        lineHeight: 1.7,
        color: "text.primary",
      }
    : {
        fontSize: (theme) => theme.typography.body2.fontSize,
        lineHeight: 1.7,
      };

  // Handle document click with debounce and loading state
  const handleDocumentClick = (doc: AssessmentDocument) => {
    // Prevent multiple clicks while loading
    if (loadingDocId) return;

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set loading state
    const docId = doc.id || doc.name;
    setLoadingDocId(docId);

    // Debounce timer - execute after 300ms
    debounceTimerRef.current = setTimeout(() => {
      onDocumentClick?.(doc);
      // Clear loading state after a short delay to show feedback
      setTimeout(() => {
        setLoadingDocId(null);
      }, 300);
    }, 300);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Skip if no value unless the field is always shown (layout dates / default_display extras)
  // Dynamic TAG concept fields always render so absent → "NA" / empty → "-".
  if (
    (value === null || value === undefined) &&
    !alwaysShowFields.includes(field.field_name) &&
    !field.default_display &&
    !(isDynamicAutoTagsField(field) && field.field_name !== "auto_tags")
  ) {
    return null;
  }

  // Format date to readable format
  const formatDate = (dateValue: unknown): string => {
    if (!dateValue) return '-';
    
    try {
      const date = dayjs(String(dateValue));
      
      // Check if date is valid
      if (!date.isValid()) {
        return String(dateValue); // Return original if invalid
      }
      
      // Format: DD-MMM-YYYY (e.g., 28-Jan-2026)
      return date.format('DD-MMM-YYYY');
    } catch {
      return String(dateValue);
    }
  };

  const renderValue = () => {
    switch (field.field_type) {
      case 'string':
        // Some backend field configs may treat date-like strings as `string`.
        // If the field name contains "date", try to format as a date anyway.
        if (
          typeof value !== "undefined" &&
          value !== null &&
          String(field.field_name).toLowerCase().includes("date")
        ) {
          return (
            <Typography variant="body1" sx={dateValueTypographySx}>
              {formatDate(value)}
            </Typography>
          );
        }
        return (
          <Typography
            variant={field.field_name === 'title' ? 'h6' : 'body1'}
            sx={{
              fontWeight: field.field_name === 'title' ? 500 : 400,
              fontSize: (theme) => theme.typography.body2.fontSize,
              lineHeight: 1.7,
            }}
          >
            {value ? String(value) : '-'}
          </Typography>
        );

      case 'date':
        return (
          <Typography variant="body1" sx={dateValueTypographySx}>
            {formatDate(value)}
          </Typography>
        );

      case 'array':
        if (field.field_name === 'associated_documents') {
          const docs = Array.isArray(value) ? (value as AssessmentDocument[]) : [];
          if (docs.length === 0) {
            return (
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                -
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                mt: 1.5,
              }}
            >
              {docs.map((doc, index: number) => {
                const docId = doc.id || doc.name;
                const isLoading = loadingDocId === docId;

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.08),
                      borderRadius: '4px',
                      pl: 1,
                      pr: 0.5,
                      py: 0.25,
                      position: 'relative',
                      opacity: isLoading ? 0.6 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <PictureAsPdfIcon 
                      sx={{ 
                        color: 'primary.main',
                        fontSize: '1rem',
                      }} 
                    />
                    <Link
                      href="#"
                      underline="hover"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontSize: (theme) => theme.typography.body2.fontSize,
                        fontWeight: 500,
                        cursor: isLoading ? 'default' : 'pointer',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pointerEvents: isLoading ? 'none' : 'auto',
                        '&:hover': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                          textDecorationColor: 'primary.main',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDocumentClick(doc);
                      }}
                    >
                      {doc.name}
                    </Link>
                    {isLoading && (
                      <CircularProgress 
                        size={14} 
                        sx={{ 
                          color: 'primary.main',
                          ml: 0.5,
                        }} 
                      />
                    )}
                    {(doc.name?.toLowerCase().endsWith('.pdf') || doc.file_path?.toLowerCase().endsWith('.pdf')) && (
                      <Tooltip title="Preview" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDocumentClick(doc)}
                          disabled={isLoading}
                          sx={{
                            ml: 0.5,
                            color: 'primary.main',
                            padding: '2px',
                          }}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Download" arrow>
                      <IconButton
                        size="small"
                        disabled={downloadingDocId === docId}
                        onClick={async () => {
                          if (downloadingDocId) return;
                          setDownloadingDocId(docId);
                          try {
                            await onDocumentDownload?.(doc);
                          } finally {
                            setDownloadingDocId(null);
                          }
                        }}
                        sx={{
                          color: 'primary.main',
                          padding: '2px',
                        }}
                      >
                        {downloadingDocId === docId ? (
                          <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                        ) : (
                          <DownloadIcon sx={{ fontSize: '1rem' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    {doc.file_path && regulationId && (
                      <Tooltip title="Open in Content Partner" arrow>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              const filePath = doc.file_path || '';
                              const fileName = filePath.split('/').pop() || doc.name;
                              
                              // Extract tenantId from file_path if it starts with s3://
                              // Example: s3://bucket/tenantId/filename.pdf -> tenantId is the 4th element (index 3)
                              let tenantId: string | number = 0;
                              if (filePath.startsWith('s3://')) {
                                const parts = filePath.split('/');
                                if (parts.length > 3) {
                                  // Remove any non-digit characters from the tenantId string
                                  tenantId = parts[3].replace(/\D/g, '');
                                }
                              }

                              const baseUrl = (await import('../../../config/env')).envConfig.apiBaseUrl;
                              const result = await apiClient.get(
                                `${baseUrl}/regulations/InsightsOS/v2.0/GetEncryptAttachmentName`,
                                {
                                  tenantId,
                                  fileName,
                                  insight_id: regulationId,
                                },
                              );
                              console.log(result,"result==============")
                              const url = (result as any)?.response ?? (result as any)?.data?.response;
                              if (url) window.open(String(url), '_blank');
                            } catch (e) {
                              console.error('Failed to get Content Partner URL:', e);
                            }
                          }}
                          sx={{
                            ml: 0.5,
                            color: 'primary.main',
                            padding: '2px',
                          }}
                        >
                          <SmsOutlinedIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        } else if (field.field_name === 'reference_links') {
          // No border here — SummarySection meta grid already draws the section divider.
          return (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}
            >
              {(value as Array<string | { name: string }>).map((link, index: number) => {
                const rawUrl = typeof link === 'string' ? link : link.name;
                const url = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9ca3af', mt: 1, flexShrink: 0 }} />
                    <Link
                      href={url}
                      target="_blank"
                      underline="hover"
                      sx={{
                        color: '#2563eb',
                        fontSize: (theme) => theme.typography.body2.fontSize,
                        wordBreak: 'break-all',
                      }}
                    >
                      {rawUrl}
                    </Link>
                  </Box>
                );
              })}
            </Box>
          );
        } else if (
          field.field_name === 'product_types' ||
          isRegulationCategoriesFieldName(field.field_name) ||
          field.field_name === 'markets'
        ) {
          const rawItems =
            field.field_name === 'product_types'
              ? visibleProductTypes((value as any[]) || [])
              : ((value as any[]) || []);
          if (field.field_name === 'product_types' && rawItems.length === 0) {
            return (
              <Typography variant="body2" sx={{ lineHeight: 1.7, fontWeight: 400 }}>
                -
              </Typography>
            );
          }
          if (field.field_name === 'product_types' && productTypesSummaryTwoLineClamp) {
            const fullText = rawItems
              .map((item) =>
                typeof item === 'string'
                  ? item.trim()
                  : String((item as { name?: string })?.name ?? '').trim(),
              )
              .filter(Boolean)
              .join(', ');
            const body = (
              <Typography
                component="div"
                variant="body2"
                sx={{
                  lineHeight: 1.5,
                  fontWeight: 400,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                }}
              >
                {fullText}
              </Typography>
            );
            return (
              <Tooltip
                title={fullText}
                arrow
                placement="top"
                enterDelay={400}
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 480,
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      lineHeight: 1.45,
                    },
                  },
                }}
              >
                {body}
              </Tooltip>
            );
          }
          const isCategories = isRegulationCategoriesFieldName(field.field_name);
          const isClickable = field.field_name === 'markets' || isCategories;
          const items = isCategories && flattenedCategoryItems ? flattenedCategoryItems : rawItems;

          return (
            <TagListWithViewAll
              items={items}
              field={field}
              isClickable={isClickable}
              maxRows={4}
              onViewAll={isClickable
                ? (_f: FieldConfig, _items: any[], flatIdx?: number) => {
                    const level1Idx =
                      flatIdx !== undefined && isCategories
                        ? categoryDisplayMeta?.level1IndexByFlat[flatIdx] ?? flatIdx
                        : flatIdx;
                    onViewAll?.(field, rawItems, level1Idx);
                  }
                : undefined
              }
              isCategories={isCategories}
            />
          );
        }

        // Generic concept arrays (Country, Department, ProductType, …)
        const genericItems = (Array.isArray(value) ? (value as any[]) : []).filter(
          (item) => {
            if (item == null) return false;
            if (typeof item === "string") return item.trim().length > 0;
            if (typeof item === "object") {
              if ((item as { is_deleted?: boolean }).is_deleted === true) {
                return false;
              }
              const label =
                (item as { name?: string }).name ??
                (item as { display_name?: string }).display_name ??
                (item as { label?: string }).label;
              return Boolean(label && String(label).trim());
            }
            return false;
          },
        );
        if (genericItems.length === 0) {
          const emptyLabel =
            autoTagsFieldPresent !== undefined && isDynamicAutoTagsField(field)
              ? autoTagEmptyDisplay(autoTagsFieldPresent)
              : "-";
          return (
            <Typography
              variant="body2"
              color={emptyLabel === "-" ? "text.secondary" : "inherit"}
              sx={{ lineHeight: 1.7, fontWeight: 400 }}
            >
              {emptyLabel}
            </Typography>
          );
        }
        const canViewAll = typeof onViewAll === "function";
        return (
          <TagListWithViewAll
            items={genericItems}
            field={field}
            isClickable={canViewAll}
            maxRows={4}
            onViewAll={canViewAll ? onViewAll : undefined}
            isCategories={false}
            viewAllOnlyWhenOverflow
          />
        );

      default:
        // For unknown types, try to display as string, or fallback to JSON
        if (typeof value === 'object' && value !== null) {
          const obj = value as any;
          // Try to extract meaningful text from object
          const displayValue = obj.username || obj.name || obj.label || obj.title;
          if (displayValue) {
            return (
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {displayValue}
              </Typography>
            );
          }
        }
        return (
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            {String(value)}
          </Typography>
        );
    }
  };

  const displayLabel = (() => {
    if (isRegulationCategoriesFieldName(field.field_name) && Array.isArray(value) && value.length > 0) {
      const flatCount = flattenedCategoryItems ? flattenedCategoryItems.length : 0;
      return `${field.display_name}  (${flatCount})`;
    }
    return field.display_name;
  })();

  const showAutoTagMark =
    isDynamicAutoTagsField(field) && field.field_name !== "auto_tags";

  return (
    <Box key={field.field_name} sx={{ mb: denseLayout ? 0 : 2 }}>
      <Typography
        variant="caption"
        gutterBottom
        sx={{
          color: "#666666",
          fontWeight: 600,
          display: 'block',
        }}
      >
        {showAutoTagMark ? (
          <AutoTagDisplayLabel label={String(displayLabel)} />
        ) : (
          displayLabel
        )}
      </Typography>
      {renderValue()}
    </Box>
  );
};
